import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns } from 'domains/MediaPlatforms/Events'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { mediaLibraryConfigRoute } from 'pages/NWPlatform/MediaLibraries/MediaLibrary'
import { mediaPlatformEventsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Events'
import { mediaAssetQueryKeys } from 'services/queryKeys/media'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const navigate = useNavigate({ from: mediaPlatformEventsOverviewRoute.fullPath })
  const { environmentId, platformId } = mediaPlatformEventsOverviewRoute.useParams()
  const { query } = mediaPlatformEventsOverviewRoute.useSearch()

  const eventsQuery = useInfiniteQuery({
    ...mediaAssetQueryKeys.eventList({
      environmentId,
      platformId,
      query,
    }),
    select: (data) => ({ flat: data.pages.flatMap((page) => page.items) }),
  })
  const events = eventsQuery.data?.flat

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:MediaPlatform.events.searchEvents')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({ queryKey: mediaAssetQueryKeys.eventList({ environmentId }).queryKey }),

            [environmentId, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        size={56}
        data={events ?? []}
        columns={columns}
        isInitialLoadingGlobal={eventsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={mediaPlatformEventsOverviewRoute.fullPath}
            // todo replace with event config route
            to={mediaLibraryConfigRoute.to}
            params={{ mediaLibraryId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(eventsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        // ActionMenuProps={(row) => ({
        //   title: row.original.name,
        //   entity: t('domain:MediaPlatform.mediaPlatform'),
        // })}
        // RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        {/* <NewDialog /> */}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
